import { Redis } from "@upstash/redis"
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

const redis = new Redis({
  url: process.env.PLASMO_PUBLIC_UPSTASH_REDIS_URL || process.env.NEXT_PUBLIC_UPSTASH_REDIS_URL,
  token: process.env.PLASMO_PUBLIC_UPSTASH_REDIS_TOKEN || process.env.NEXT_PUBLIC_UPSTASH_REDIS_TOKEN,
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const isFirefox = () => /firefox/i.test(navigator.userAgent)

export async function setUserSetting(userId: string, settingKey: string, value: any) {
  try {
    const key = `user:${userId}:settings`
    
    const type = await redis.type(key)
    if (type !== 'hash') {
      await migrateUserSettings(userId)
    }
    
    await redis.hset(key, {
      [settingKey]: typeof value === 'string' ? value : JSON.stringify(value)
    })
    
    const oldKey = `user:${userId}:${settingKey}`
    await redis.del(oldKey)
  } catch (error) {
    throw error
  }
}

export async function getAllUserSettings(userId: string) {
  const key = `user:${userId}:settings`
  const type = await redis.type(key)
  
  if (type !== 'hash') {
    await migrateUserSettings(userId)
  }
  
  const settings = await redis.hgetall(key)
  const processedSettings: Record<string, any> = {}

  const defaultSettings = {
    languageSetting: 'zh',
    targetLanguageSetting: 'en',
    cefrLevel: '',
    autoReadSetting: false,
    autoTranslateSetting: false,
    autoPlaySetting: false,
    autoTranslateLevel: '',
    myVocabularySetting: true,
    dictionarySetting: '',
    favorites: [],
    playlists: {},
    preferChineseTitles: false,
    subscribedPodcastsSnapshot: {}
  }

  if (!settings) {
    return defaultSettings
  }

  for (const [fieldKey, value] of Object.entries(settings)) {
    if (!value) {
      continue
    }
    
    if (value === 'true' || value === 'false') {
      processedSettings[fieldKey] = value === 'true'
    } else if (value === 'null') {
      processedSettings[fieldKey] = null
    } else {
      try {
        processedSettings[fieldKey] = JSON.parse(value as string)
      } catch {
        processedSettings[fieldKey] = value
      }
    }
  }

  return { ...defaultSettings, ...processedSettings }
}

export async function migrateUserSettings(userId: string) {
  const oldSettings = [
    'autoReadSetting',
    'autoTranslateSetting',
    'languageSetting',
    'autoPlaySetting',
    'myVocabularySetting',
    'dictionarySetting'
  ]

  const newKey = `user:${userId}:settings`
  
  const newKeyType = await redis.type(newKey)
  if (newKeyType !== 'none' && newKeyType !== 'hash') {
    await redis.del(newKey)
  }

  const settings: Record<string, any> = {}

  for (const setting of oldSettings) {
    const oldKey = `user:${userId}:${setting}`
    const value = await redis.get(oldKey)
    if (value !== null) {
      settings[setting] = value
      await redis.del(oldKey)
    }
  }

  if (Object.keys(settings).length > 0) {
    await redis.hset(newKey, settings)
  }
}

// Podcast subscription management functions
interface PodcastSnapshot {
  id: string
  title: string
  author?: string
  imageUrl?: string
  feedUrl?: string
  defaultLevelRange?: string[] | string | null
  updatedAt?: string
}

function parseSnapshotMap(raw: any): Record<string, PodcastSnapshot> {
  if (!raw) return {}
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, PodcastSnapshot>
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, PodcastSnapshot>
      }
    } catch (error) {
      console.error('Failed to parse subscribedPodcastsSnapshot:', error)
    }
  }
  return {}
}

export async function getSubscribedPodcastIds(userId: string): Promise<string[]> {
  try {
    const key = `user:${userId}:settings`
    const subscribedIdsStr = await redis.hget(key, 'subscribedPodcastIds')
    
    if (!subscribedIdsStr) {
      return []
    }
    
    // Check if it's already an array (Redis HASH can return arrays directly)
    if (Array.isArray(subscribedIdsStr)) {
      return subscribedIdsStr
    }
    
    // Try to parse as JSON string
    try {
      const parsed = JSON.parse(subscribedIdsStr as string)
      return Array.isArray(parsed) ? parsed : []
    } catch (parseError) {
      // Handle array-like string format: [ 'item1', 'item2' ]
      if (typeof subscribedIdsStr === 'string') {
        const trimmed = subscribedIdsStr.trim()
        
        if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
          try {
            // Remove outer brackets and split by comma
            const content = trimmed.slice(1, -1).trim()
            
            if (!content) {
              return []
            }
            
            // Split by comma and clean each item
            const items = content.split(',').map(item => {
              return item.trim().replace(/^['"`]|['"`]$/g, '') // Remove quotes
            }).filter(item => item.length > 0)
            
            return items
          } catch (arrayError) {
            console.error('Error parsing array-like string:', arrayError)
          }
        }
      }
      
      console.error('Failed to parse subscribed podcast IDs:', parseError)
      return []
    }
  } catch (error) {
    console.error('Error getting subscribed podcast IDs:', error)
    return []
  }
}

export async function getSubscribedPodcastSnapshots(userId: string): Promise<Record<string, PodcastSnapshot>> {
  try {
    const key = `user:${userId}:settings`
    const snapshotRaw = await redis.hget(key, 'subscribedPodcastsSnapshot')
    return parseSnapshotMap(snapshotRaw)
  } catch (error) {
    console.error('Error getting subscribed podcast snapshots:', error)
    return {}
  }
}

export async function togglePodcastSubscription(
  userId: string,
  podcastId: string,
  snapshot?: PodcastSnapshot | null
): Promise<{ isSubscribed: boolean, subscribedPodcastIds: string[], subscribedPodcastsSnapshot: Record<string, PodcastSnapshot> }> {
  try {
    const key = `user:${userId}:settings`
    
    // Get current subscribed podcast IDs
    let subscribedPodcastIds = await getSubscribedPodcastIds(userId)
    const snapshotMap = await getSubscribedPodcastSnapshots(userId)
    
    // Check if already subscribed
    const isCurrentlySubscribed = subscribedPodcastIds.includes(podcastId)
    
    if (isCurrentlySubscribed) {
      // Unsubscribe: remove from array
      subscribedPodcastIds = subscribedPodcastIds.filter(id => id !== podcastId)
      if (snapshotMap[podcastId]) {
        delete snapshotMap[podcastId]
      }
    } else {
      // Subscribe: add to array
      subscribedPodcastIds.push(podcastId)
      if (snapshot) {
        snapshotMap[podcastId] = {
          ...snapshot,
          id: podcastId,
          updatedAt: new Date().toISOString()
        }
      }
    }
    
    // Save updated list to settings hash
    await redis.hset(key, {
      subscribedPodcastIds: JSON.stringify(subscribedPodcastIds),
      subscribedPodcastsSnapshot: JSON.stringify(snapshotMap)
    })
    
    return {
      isSubscribed: !isCurrentlySubscribed,
      subscribedPodcastIds,
      subscribedPodcastsSnapshot: snapshotMap
    }
  } catch (error) {
    console.error('Error toggling podcast subscription:', error)
    throw error
  }
}

export async function isPodcastSubscribed(userId: string, podcastId: string): Promise<boolean> {
  try {
    const subscribedPodcastIds = await getSubscribedPodcastIds(userId)
    return subscribedPodcastIds.includes(podcastId)
  } catch (error) {
    console.error('Error checking podcast subscription:', error)
    return false
  }
}

export async function getSubscribedPodcasts(userId: string, podcastIds: string[]): Promise<string[]> {
  try {
    if (podcastIds.length === 0) return []
    
    const subscribedPodcastIds = await getSubscribedPodcastIds(userId)
    
    // Return intersection of provided IDs and subscribed IDs
    return podcastIds.filter(id => subscribedPodcastIds.includes(id))
  } catch (error) {
    console.error('Error getting subscribed podcasts:', error)
    return []
  }
}
